import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import i18n from "i18n";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AsyncPaginate, wrapMenuList } from "react-select-async-paginate";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import barListApi from "api/barListApi";
import { ceil } from "lodash";

interface Option {
  label: string | any;
  value: string;
}
interface DropdownOptionProps {
  name?: string;
  listCheckedBarType?: string | null;
  debounceTimeout?: number;
  onChangeSelect?: (params: Option) => void;
  isMulti?: boolean;
  placeholder?: string;
  isShowIconCheck?: boolean;
  hideSelectedOptions?: boolean;
  isAllOption?: boolean;
  isClearable?: boolean
}
const LIMIT = 10;

const DropdownAsyncBarType = ({
  name = "",
  listCheckedBarType,
  debounceTimeout = 700,
  onChangeSelect,
  isMulti = false,
  placeholder,
  isShowIconCheck = false,
  hideSelectedOptions = true,
  isAllOption = false,
  isClearable = true
}: DropdownOptionProps) => {
  const { t } = useTranslation();
  const [isAllCheckedUsers, setIsAllCheckedUsers] = useState(false);

  const OPTION_ALL_DEFAULT = { label: t("All Filter"), value: "all" };

  const [options, setOptions] = useState([])

  const setOnChangeOption = (item: any) => {
    onChangeSelect && onChangeSelect(item);
  };

  const loadListBars = async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search?: string;
  }) => {
    const {
      data: { list, meta },
      code,
    } = await barListApi.getBarsType({ page, limit, keyword: search });

    if (code != 200) {
      return { success: false, dataUsersOptions: [], lastPage: 0 };
    }

    const dataUsersOptions = list?.map((a: any) => {
      return {
        value: String(a?.id),
        label: (
          <div className="d-flex align-list-center gap-2">
            {/* <div>{`${a?.name?.en} - ${a?.name?.ko}`}</div> */}
            <div>{i18n?.language === "ko" ? `${a?.name?.ko}` : `${a?.name?.en}`}</div>
          </div>
        ),
        data: a,
      };
    });
    return {
      success: true,
      dataUsersOptions,
      lastPage: ceil(meta?.total / meta?.limit)
    };
  };

  async function loadOptions(
    search: string,
    loadedOptions: any[],
    { page }: { page: number }
  ) {
    const { success, dataUsersOptions, lastPage } = await loadListBars({
      page,
      limit: LIMIT,
      search,
    });

    if (!success) {
      toast(`${t("Failed to load data")}`, CONFIG_OPTION_TOAST_ERROR);
      return {
        options: [],
        hasMore: false,
      };
    }

    let options = dataUsersOptions;

    setOptions((_prev: any) => options);

    return {
      options: options,
      hasMore: lastPage > page,
      additional: {
        page: page + 1,
      },
    };
  }

  const MenuList = (props: any) => {
    let childComponent = props?.children;
    if (props?.children?.length > 0) {
      const allOption = props?.children?.filter((item: any) => item?.props?.value == 'all');
      let fixedIndex = -1;
      if (allOption) {
        fixedIndex = props?.children.indexOf(allOption?.[0]);
      }
      const fixedElement = props?.children?.[fixedIndex];

      const childComponentFormat = ((fixedIndex != -1 ? props?.children.slice(1) : props?.children) || [])?.sort((a: any, b: any) => {
        let pointA = a.props.isSelected === true ? 1 : 0;
        let pointB = b.props.isSelected === true ? 1 : 0;
        return pointB - pointA;
      })

      if (fixedElement) {
        childComponent = [fixedElement, ...childComponentFormat]
      } else {
        childComponent = childComponentFormat;
      }
    }

    return (
      <components.MenuList {...props}>{childComponent}</components.MenuList>
    );
  }

  const Option = (props: any) => {
    if (props?.isSelected && props?.value == "all") {
      setIsAllCheckedUsers(true);
    } else if (!props?.isSelected && props?.value == "all") {
      setIsAllCheckedUsers(false);
    }

    return (
      <components.Option {...props} style={{ zIndex: 999 }}>
        <div className="d-flex align-items-center gap-2">
          {((props?.isSelected || isAllCheckedUsers) && isShowIconCheck) ? (
            <i className="ri-check-line align-middle fs-14" />
          ) : (
            ""
          )}
          {props.data?.label}
        </div>
      </components.Option>
    );
  }

  const MenuListAsync = wrapMenuList(MenuList);


  const optionsMemo = useMemo(() => {
    return [...(isAllOption ? [OPTION_ALL_DEFAULT] : []), ...options];
  }, [JSON.stringify(options), isAllOption, i18n?.language]);

  useEffect(() => {
    if (((listCheckedBarType as any)?.value === OPTION_ALL_DEFAULT?.value || !(listCheckedBarType as any)?.value) && isAllOption) {
      setOnChangeOption(OPTION_ALL_DEFAULT)
    }
  }, [i18n.language, isAllOption, JSON.stringify(listCheckedBarType)]);

  return (
    <AsyncPaginate
      key={i18n.language}
      name={name}
      isMulti={isMulti}
      isClearable={isClearable}
      // defaultOptions
      options={optionsMemo}
      debounceTimeout={debounceTimeout}
      value={listCheckedBarType}
      loadOptions={loadOptions}
      placeholder={placeholder}
      className="dropdown-status-rounded"
      hideSelectedOptions={false}
      onChange={(e: any) => setOnChangeOption(e)}
      additional={{
        page: 1,
      }}
      formatOptionLabel={(option: any) => {
        return option?.value != "all" ? (
          <div className="d-flex align-list-center gap-2">
            <div>{i18n?.language === "ko" ? `${option?.data?.name?.ko}` : `${option?.data?.name?.en}`}</div>
          </div>) :
          (
            t(option?.label)
          )
      }}
      // components={{
      //   Option: Option,
      //   MenuList: MenuListAsync,
      // }}
      closeMenuOnSelect={hideSelectedOptions}
      menuPortalTarget={document.body}
      styles={{
        option: (styles: any) => {
          let styleNew = isMulti ? {
            ...styles,
            backgroundColor: "white",
            zIndex: 99,
            color: "black",
          } : styles;

          return styleNew;
        },
        control: (styles: any, { isFocused }: any) => {
          return {
            ...styles,
            borderColor: "#e9ebec",
            borderRadius: "0.6rem",
            color: "white",
            "&:hover": { borderColor: "#009ef7" },
          };
        },
        multiValue: (styles: any) => {
          return {
            ...styles,
            backgroundColor: "#009ef7",
          };
        },
        multiValueLabel: (styles: any) => ({
          ...styles,
          color: "white !important",
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 1051,
        }),
      }}
    />
  );
};

export default DropdownAsyncBarType;
